import { useAuthStore } from '@/stores/auth'

/** Matches backend `MAX_BUG_REPORT_TEXT_CHARS` / createBugReportLambda. */
export const MAX_BUG_REPORT_TEXT_CHARS = 4000

export interface BugReportScreenshotUpload {
  key: string
}

export interface CreateBugReportPayload {
  message: string
  expectedBehavior?: string
  source: 'shake' | 'manual'
  profileId?: string
  routePath?: string
  routeName?: string
  appVersion?: string
  platform?: string
  devicePlatform?: string
  userAgent?: string
  posthogDistinctId?: string
  posthogSessionId?: string
  screenshot?: {
    included: boolean
    key?: string
  }
}

const MAX_BUG_REPORT_SCREENSHOT_BYTES = 5 * 1024 * 1024

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [metadata, data] = dataUrl.split(',')
  const contentType = metadata.match(/:(.*?);/)?.[1] || 'image/png'
  const binary = atob(data)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return new File([bytes], filename, { type: contentType })
}

export async function uploadBugReportScreenshot(
  dataUrl: string,
): Promise<BugReportScreenshotUpload> {
  const authStore = useAuthStore()
  const file = dataUrlToFile(dataUrl, `bug-report-${Date.now()}.png`)

  if (file.size > MAX_BUG_REPORT_SCREENSHOT_BYTES) {
    throw new Error('Bug report screenshot must be smaller than 5 MB')
  }

  const uploadResponse = await authStore.axios.post(
    '/bug-reports/attachments',
    {
      contentType: file.type,
      sizeBytes: file.size,
    },
  )

  const screenshotResponse = await fetch(uploadResponse.data.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  })

  if (!screenshotResponse.ok) {
    const responseBody = await screenshotResponse
      .text()
      .catch(() => '<unreadable>')
    throw new Error(
      `Bug report screenshot upload failed: ${screenshotResponse.status} ${screenshotResponse.statusText} ${responseBody}`,
    )
  }

  return {
    key: uploadResponse.data.key,
  }
}

export async function createBugReport(payload: CreateBugReportPayload) {
  const authStore = useAuthStore()
  const response = await authStore.axios.post('/bug-reports', payload)
  return response.data
}
